import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";
import { usePolling } from "@/components/croc/hooks";
import { Panel, SectionTitle, StatCard } from "@/components/croc/ui";
import { fmtUsd, shortAddr } from "@/lib/format";
import api from "@/lib/api";
import { Copy, Users, Gift, Link2, Share2, UserCheck } from "lucide-react";
import { RankShareCard } from "@/components/croc/RankShareCard";
import { MarketingPoints } from "@/components/croc/MarketingPoints";
import { MarketingFeed } from "@/components/croc/MarketingFeed";

export default function Referral() {
  const { address, user } = useWallet();
  const { data } = usePolling(() => api.referrals(address), [address], 30000);
  const { data: directs } = usePolling(() => api.chainDirects(address || "0x0"), [address], 30000);
  const code = user?.referral_code || "";
  const link = `${window.location.origin}/?ref=${code}`;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div>
      <SectionTitle
        eyebrow="Direct Sponsor (5%) & Referral Hub"
        title="Referral Hub"
        subtitle="Earn 5% of every direct referral's stake — paid from the revenue pool, never from principal."
        testid="referral-title"
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Direct Referrals" value={data?.direct_count ?? 0} icon={UserCheck} accent="emerald" testid="stat-directs" />
        <StatCard label="Direct Bonus" value={fmtUsd(user?.direct_bonus_earned)} icon={Gift} accent="gold" testid="stat-direct-bonus" />
        <StatCard label="Override Income" value={fmtUsd(user?.level_income_earned)} icon={Users} accent="emerald" testid="stat-level-income" />
        <StatCard label="Team Volume" value={fmtUsd(user?.team_volume)} icon={Share2} accent="gold" testid="stat-team-volume" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2"><MarketingPoints /></div>
        <div className="lg:col-span-1"><MarketingFeed /></div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-1"><RankShareCard /></div>
        <Panel className="lg:col-span-2" data-testid="referral-directs">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><UserCheck className="w-4 h-4 emerald-text" /><span className="font-display font-bold text-white">Your Directs · On-chain contribution</span></div>
            <span className="text-xs text-muted-foreground">Total {fmtUsd(directs?.total_contribution ?? 0)}</span>
          </div>
          <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
            {(directs?.directs ?? []).map((d, i) => (
              <div key={d.address} className="flex items-center justify-between rounded-xl border border-white/10 bg-[#090E0B] px-4 py-2.5" data-testid={`direct-row-${i}`}>
                <span className="font-num text-sm text-white">{shortAddr(d.address)}</span>
                <span className="font-num text-sm emerald-text">{fmtUsd(d.team_volume)}</span>
              </div>
            ))}
            {(directs?.directs?.length ?? 0) === 0 && <div className="text-center py-8 text-muted-foreground text-sm">No on-chain directs yet — share your link to grow your team.</div>}
          </div>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel glow="gold" data-testid="referral-link-panel">
          <div className="flex items-center gap-2 mb-4">
            <Link2 className="w-4 h-4 gold-text" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Your Referral Link</span>
          </div>
          <div className="bg-white p-3 rounded-xl w-fit mx-auto mb-4">
            <QRCodeSVG value={link} size={148} bgColor="#ffffff" fgColor="#0F1712" level="M" />
          </div>
          <div className="rounded-xl border border-white/10 bg-[#090E0B] p-3 mb-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Referral Code</div>
            <div className="flex items-center justify-between">
              <span className="font-num font-bold gold-text">{code}</span>
              <button data-testid="referral-code-copy-button" onClick={() => copy(code, "Code")} className="text-muted-foreground hover:text-white">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
          <button
            data-testid="referral-link-copy-button"
            onClick={() => copy(link, "Referral link")}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#1CFD4C] py-3 font-semibold text-[#050806] hover:bg-[#4DFF74] transition-colors"
          >
            <Copy className="w-4 h-4" /> Copy Referral Link
          </button>
        </Panel>

        <Panel className="lg:col-span-2" data-testid="referral-team">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 emerald-text" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Your Direct Team</span>
          </div>
          {(data?.directs?.length ?? 0) === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No referrals yet. Share your link to start earning the 5% direct bonus.
            </div>
          ) : (
            <div className="space-y-2">
              {data.directs.map((d, i) => (
                <div key={d.address} className="flex items-center justify-between rounded-xl border border-white/10 bg-[#090E0B] px-4 py-3" data-testid={`direct-row-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#10B981]/15 grid place-items-center text-xs font-num emerald-text">{i + 1}</div>
                    <span className="font-num text-white">{shortAddr(d.address)}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-num text-white">{fmtUsd(d.staked)}</div>
                    <div className="text-[10px] text-muted-foreground">staked</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
